/**
 * NotesApplication - Main application class for NotesApp_v2
 * Simplified note-taking application
 */

import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig, SerializedNode } from '../../../services/baseApplication/types';
import { NOTES_APP_CONFIG, isNoteClassName } from './NotesConfig';
import { createCollaborationProvider } from '../../../services/collaboration';
import { authService } from '../../../services/auth/authService';
import { entityService } from '../../../services/entityService';
import { generateUserColor } from '../../shared/appHelpers';

export class NotesApplication extends BaseApplication {
  // ✅ collaborationProvider inherited from BaseApplication
  private entityName: string = '';
  
  constructor() {
    super();
    // ✅ REQUIRED: Disable suggestion mode for Notes
    // Insert/replace operations still check suggestionMode - keep false to avoid marking elements
    this.suggestionMode = false;
  }
  
  /**
   * Get application configuration
   */
  protected getConfig(): AppConfig {
    return NOTES_APP_CONFIG;
  }

  protected getWebSocketRoomPrefix(): string {
    return 'note';
  }
  
  /**
   * Load note from backend
   * ✅ UPDATED: Now uses BaseApplication unified persistence pattern
   */
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    console.log('🚀 [NotesApplication] === LOADING NOTE ===', entityId);
    
    const entity = await entityService.get(entityId);
    if (!entity) throw new Error(`Note not found: ${entityId}`);
    
    this.entityName = entity.name || 'Untitled Note';
    
    console.log('📦 [NotesApplication] Entity fetched:', {
      name: entity.name,
      entityType: entity.entityType,
      hasBase64: !!entity.skeleton?.base64,
      base64Length: entity.skeleton?.base64?.length || 0,
      version: entity.skeleton?.version,
      architecture: entity.skeleton?.architecture
    });
    
    // Initialize (sets entityId, creates base structures)
    await this.initialize(entityId);
    console.log('⚙️  [NotesApplication] Initialized, entityId set to:', this.entityId);
    
    // ✅ NEW PATTERN: Create own Y.Doc (self-contained, not shared)
    console.log('📥 [NotesApplication] Creating self-contained Y.Doc...');
    this.ydoc = new Y.Doc();
    console.log('📥 [NotesApplication] Y.Doc created, clientID:', this.ydoc.clientID);
    
    // ✅ NEW PATTERN: Load base64 state directly (if exists) with error handling
    if (entity.skeleton?.base64 && this.ydoc) {
      console.log('📥 [NotesApplication] Loading base64 state...');
      try {
        this.loadBase64State(entity.skeleton.base64);
        console.log('✅ [NotesApplication] Base64 state loaded');
      } catch (error) {
        console.error('❌ [NotesApplication] Failed to load Y.js state:', error);
        this.emit('loadError', { 
          error, 
          message: 'Failed to load note data. Starting with empty note.' 
        });
        // Continue with empty note rather than failing completely
        console.log('⚠️  [NotesApplication] Continuing with empty note');
      }
    } else {
      console.log('ℹ️  [NotesApplication] No base64 state to load (new note)');
    }
    
    // Get Y.js containers
    if (this.ydoc) {
      // ✅ CRITICAL: Use 'prosemirror' fragment name to match AI write commands
      this.yxml = this.ydoc.getXmlFragment('prosemirror');
      console.log('📋 [NotesApplication] Got prosemirror fragment, length:', this.yxml.length);
      
      // 🔍 DEBUG: Inspect fragment content
      if (this.yxml.length > 0) {
        console.log('🔍 [NotesApplication] Fragment content:');
        for (let i = 0; i < this.yxml.length; i++) {
          const child = this.yxml.get(i);
          console.log(`  Element ${i}:`, {
            type: child?.constructor?.name,
            text: child?.toString ? child.toString().slice(0, 100) : 'N/A'
          });
        }
      }
      
      // Initialize empty note if needed
      if (this.yxml.length === 0 && !entity.skeleton?.base64) {
        console.log('🆕 [NotesApplication] Empty note, initializing...');
        this.initializeEmptyNote();
      } else if (this.yxml.length === 0 && entity.skeleton?.base64) {
        console.error('🚨 [NotesApplication] Fragment is empty even though base64 exists! This is a bug!');
      }
      
      // ✅ FIX (Issue #5): Setup auto-save BEFORE building indices
      // This ensures migrations get auto-saved
      console.log('[NotesApplication] 🔧 Setting up auto-save...');
      this.setupAutoSave();
      console.log('[NotesApplication] ✅ Auto-save enabled');
      
      // Build indices (migrations will now trigger auto-save)
      await this.buildCoreIndices();
      console.log('📇 [NotesApplication] Core indices built, total nodes:', this.byIdIndex.size);
      
      // ✅ MIGRATION: Auto-assign IDs to existing content without IDs
      // Only run if index is empty (indicates content without IDs)
      if (this.byIdIndex.size === 0 && this.yxml && this.yxml.length > 0) {
        console.log('[NotesApp] 🔄 MIGRATION NEEDED: Content exists but no indexed nodes');
        await this.assignMissingIds();
        
        // Rebuild indices after migration
        await this.buildCoreIndices();
        console.log('📇 [NotesApplication] Core indices rebuilt after migration, total nodes:', this.byIdIndex.size);
      } else if (this.byIdIndex.size > 0) {
        console.log('[NotesApp] ✅ No migration needed - content already has IDs');
      }
    }
    
    // ✅ NEW PATTERN: Optional WebSocket collaboration (real-time sync, not required for persistence)
    if (!options.offline && this.ydoc) {
      console.log('[NotesApplication] 🔌 Setting up optional WebSocket collaboration...');
      await this.setupCollaborationWithDoc(entityId, this.ydoc);
      console.log('[NotesApplication] ✅ WebSocket collaboration enabled');
    } else {
      console.log('[NotesApplication] ⏭️  Skipping WebSocket (offline mode or no ydoc)');
    }
    
    console.log('✅ [NotesApplication] === NOTE LOAD COMPLETE ===');
  }
  
  /**
   * Migration: Auto-assign IDs to existing content without IDs
   * This ensures old manually-typed content becomes indexable
   * 
   * OPTIMIZATION: Only called when byIdIndex is empty but content exists
   */
  private async assignMissingIds(): Promise<void> {
    if (!this.yxml || !this.ydoc) return;
    
    console.log('[NotesApp] 🔄 MIGRATION: Assigning IDs to existing content...');
    let assignedCount = 0;
    
    // Use Y.js transaction to batch all ID assignments
    this.ydoc.transact(() => {
      for (let i = 0; i < this.yxml!.length; i++) {
        const child = this.yxml!.get(i);
        
        if (child instanceof Y.XmlElement) {
          const id = child.getAttribute('id');
          
          if (!id || id === 'undefined' || id === 'null') {
            // Generate new ID using same pattern as BaseApplication
            const newId = this.generateId();
            child.setAttribute('id', newId);
            assignedCount++;
            
            // Extract text for logging (only in dev)
            if (process.env.NODE_ENV === 'development') {
              let textPreview = '';
              if (child.length > 0) {
                const firstChild = child.get(0);
                if (firstChild && 'toString' in firstChild) {
                  textPreview = firstChild.toString().substring(0, 30);
                }
              }
              
              console.log('[NotesApp] ✅ Assigned ID:', {
                node: child.nodeName,
                id: newId,
                text: textPreview
              });
            }
          }
        }
      }
    }, 'notes-migration');
    
    console.log(`[NotesApp] 🎉 MIGRATION COMPLETE: Assigned ${assignedCount} ID(s)`);
  }
  
  /**
   * Set up collaboration with optional WebSocket and proper cleanup
   * ✅ UPDATED: WebSocket now optional for real-time sync, not required for persistence
   * ✅ FIX: Prevent memory leaks by cleaning up listeners and timeout
   */
  private async setupCollaborationWithDoc(entityId: string, ydoc: Y.Doc): Promise<void> {
    const authUser = authService.getCurrentUser();
    if (!authUser) {
      throw new Error('Not authenticated');
    }
    
    const currentUser = {
      id: authUser.id,
      name: authUser.name || authUser.email || 'User',
      color: generateUserColor(authUser.id)
    };
    
    this.collaborationProvider = createCollaborationProvider({
      documentId: `note:${entityId}`,
      user: currentUser,
      websocketUrl: this.getWebSocketUrl(),
      authToken: '',
      offline: false,
      doc: ydoc,
    });
    
    this.collaborationProvider.connect();
    
    // ✅ FIX: Properly cleanup timeout and event listeners to prevent memory leaks
    await new Promise<void>((resolve) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let syncedHandler: (() => void) | null = null;
      let errorHandler: ((error: Error) => void) | null = null;
      
      // ✅ Cleanup function to remove all listeners and timeout
      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (syncedHandler && this.collaborationProvider) {
          this.collaborationProvider.off('synced', syncedHandler);
          syncedHandler = null;
        }
        if (errorHandler && this.collaborationProvider) {
          this.collaborationProvider.off('error', errorHandler);
          errorHandler = null;
        }
      };
      
      // Timeout handler
      timeoutId = setTimeout(() => {
        console.log('⏱️ [NotesApplication] WebSocket sync timeout (5s) - proceeding anyway');
        cleanup();
        resolve();
      }, 5000);
      
      // Success handler
      syncedHandler = () => {
        console.log('✅ [NotesApplication] WebSocket synced');
        cleanup();
        resolve();
      };
      
      // Error handler
      errorHandler = (error: Error) => {
        console.error('❌ [NotesApplication] WebSocket sync error:', error);
        cleanup();
        resolve(); // Don't fail - continue with local state
      };
      
      // Attach handlers
      this.collaborationProvider!.on('synced', syncedHandler);
      this.collaborationProvider!.on('error', errorHandler);
    });
    
    console.log('[NotesApplication] ✅ Collaboration ready');
  }
  
  /**
   * Initialize empty note
   */
  private initializeEmptyNote(): void {
    if (!this.yxml) return;
    
    console.log('🔧 [initializeEmptyNote] Creating blank note...');
    
    // Create empty paragraph (blank note)
    const paragraph = new Y.XmlElement('paragraph');
    paragraph.setAttribute('id', this.generateId());
    paragraph.setAttribute('className', '');
    
    // Create empty text node (NO DEFAULT TEXT!)
    const text = new Y.XmlText();
    
    // Insert empty text into paragraph
    paragraph.insert(0, [text]);
    
    // Insert into document
    this.yxml.insert(0, [paragraph]);
    
    console.log('✅ [initializeEmptyNote] Created blank paragraph (no default text)');
  }
  
  /**
   * Generate unique ID
   */

  /**
   * Notes-specific: Get all tags used in the note
   */
  public getAllTags(): string[] {
    const tags = new Set<string>();
    
    for (const node of this.byIdIndex.values()) {
      if (node.attrs?.className === 'note-tag' && node.attrs?.semantic?.tag) {
        tags.add(node.attrs.semantic.tag);
      }
    }

    return Array.from(tags).sort();
  }

  /**
   * Notes-specific: Get all checklist items
   */
  public getAllChecklistItems(): Array<{ node: SerializedNode; checked: boolean }> {
    const items: Array<{ node: SerializedNode; checked: boolean }> = [];

    for (const node of this.byIdIndex.values()) {
      if (node.attrs?.className === 'note-checklist') {
        items.push({
          node: node as any,
          checked: Boolean(node.attrs.semantic?.checked)
        });
      }
    }

    return items;
  }

  /**
   * Notes-specific: Get note statistics
   */
  public getNoteStats() {
    const allNodes = Array.from(this.byIdIndex.values());
    const checklistItems = this.getAllChecklistItems();
    
    return {
      totalElements: allNodes.length,
      textElements: this.getNodesByClassName('note-text').length,
      lists: this.getNodesByClassName('note-bullet-list').length + 
             this.getNodesByClassName('note-numbered-list').length,
      checklists: checklistItems.length,
      completedTasks: checklistItems.filter(item => item.checked).length,
      tags: this.getAllTags().length,
      images: this.getNodesByClassName('note-image').length,
      codeBlocks: this.getNodesByClassName('note-code').length,
    };
  }

  /**
   * Notes-specific: Export to plain text
   */
  public exportToPlainText(): string {
    const allNodes = Array.from(this.byIdIndex.values()).filter(node => 
      isNoteClassName(node.attrs?.className || '')
    );

    const lines: string[] = [];

    for (const node of allNodes) {
      const text = this.extractTextFromNode(node as any);
      if (text) {
        lines.push(text);
        lines.push('');
      }
    }

    return lines.join('\n');
  }

  /**
   * Notes-specific: Export to markdown
   */
  public exportToMarkdown(): string {
    const allNodes = Array.from(this.byIdIndex.values()).filter(node => 
      isNoteClassName(node.attrs?.className || '')
    );

    const lines: string[] = [];

    for (const node of allNodes) {
      const className = node.attrs?.className;
      const text = this.extractTextFromNode(node as any);
      
      if (!text) continue;

      if (className === 'note-title') {
        lines.push(`# ${text}`);
      } else if (className === 'note-text') {
        lines.push(text);
      } else if (className === 'note-quote') {
        lines.push(`> ${text}`);
      } else if (className === 'note-code') {
        lines.push('```');
        lines.push(text);
        lines.push('```');
      } else if (className === 'note-checklist') {
        const checked = node.attrs?.semantic?.checked ? 'x' : ' ';
        lines.push(`- [${checked}] ${text}`);
      } else if (className === 'note-bullet-list') {
        lines.push(`- ${text}`);
      } else if (className === 'note-numbered-list') {
        lines.push(`1. ${text}`);
      } else if (className === 'note-divider') {
        lines.push('---');
      } else {
        lines.push(text);
      }

      lines.push('');
    }

    return lines.join('\n');
  }

  /**
   * Helper: Extract text from node
   */
  private extractTextFromNode(node: SerializedNode): string {
    if (node.text) return node.text;
    if (node.content) {
      return node.content.map(child => this.extractTextFromNode(child)).join(' ');
    }
    return '';
  }

  /**
   * Get the current entity name (title)
   */
  public getEntityName(): string {
    return this.entityName;
  }

  /**
   * Internal setter for entity name (doesn't call backend)
   * Used when external update already happened (e.g., sidebar rename)
   */
  public _setEntityName(newName: string): void {
    this.entityName = newName;
    console.log('📝 [NotesApplication] Entity name cache updated:', newName);
  }

  /**
   * Return the Y.Doc instance for this note.
   * Used by the content-reload listener in NotesApp_v2.tsx to merge
   * freshly fetched base64 into the live Y.Doc after an agent write.
   */
  public getYDoc(): Y.Doc | null {
    return this.ydoc ?? null;
  }

  /**
   * Update the entity name (title)
   * Uses entityStore.actions to ensure events are emitted
   */
  public async updateEntityName(newName: string): Promise<void> {
    const trimmedName = newName.trim();
    const finalName = trimmedName || 'Untitled Note';
    
    this.entityName = finalName;
    
    if (!this.entityId) {
      console.warn('[NotesApplication] Cannot update entity name - no entityId');
      return;
    }
    
    // ✅ CRITICAL: Use entityStore.actions.updateEntity to emit events!
    // This ensures sidebar and other components get notified of the change
    const { default: useEntityStore } = await import('../../../stores/entityStore');
    await useEntityStore.getState().actions.updateEntity(this.entityId, { 
      name: finalName 
    });
    console.log('✅ [NotesApplication] Entity name updated via entityStore.actions:', finalName);
  }
}

